import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CloudOff, Loader2, Radar, Trophy } from "lucide-react";
import { toast } from "react-toastify";
import AdminDesk from "../components/AdminDesk";
import AdminFloorGrid from "../components/AdminFloorGrid";
import AdminLeaguesChrome from "../leagues/AdminLeaguesChrome";
import AdminLeaguesFiltersRail from "../leagues/AdminLeaguesFiltersRail";
import AddLeagueModal from "../leagues/AddLeagueModal";
import LeaguesFloorIndex from "../leagues/floor/LeaguesFloorIndex";
import LeaguesFloorDetail from "../leagues/floor/LeaguesFloorDetail";
import LeagueCandidatesIndex from "../leagues/floor/LeagueCandidatesIndex";
import LeagueCandidateDetail from "../leagues/floor/LeagueCandidateDetail";
import {
  discoverAdminLeagueCandidates,
  fetchAdminLeagueCandidates,
  fetchAdminLeagueDesk,
  updateAdminLeague,
} from "../leagues/api/fetchAdminLeagues";
import "../styles/admin-markets.css";
import "../styles/admin-markets-floor.css";
import "../leagues/admin-leagues.css";

const PAGE_SIZE = 25;

export default function LeaguesPage() {
  const [deskMode, setDeskMode] = useState("managed");
  const [status, setStatus] = useState("all");
  const [seasonPhase, setSeasonPhase] = useState("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [leagues, setLeagues] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  });
  const [selectedId, setSelectedId] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [candidateSummary, setCandidateSummary] = useState(null);
  const [candidatePagination, setCandidatePagination] = useState({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  });
  const [candidatePage, setCandidatePage] = useState(1);
  const [candidateSelectedId, setCandidateSelectedId] = useState(null);
  const [candidateLoading, setCandidateLoading] = useState(false);
  const [discovering, setDiscovering] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 320);
    return () => window.clearTimeout(timer);
  }, [search]);

  const load = useCallback(
    async ({ pageNum = 1, refresh = false } = {}) => {
      setLoading(true);
      if (refresh) setRefreshing(true);
      setError(null);

      try {
        const data = await fetchAdminLeagueDesk({
          page: pageNum,
          limit: PAGE_SIZE,
          status,
          seasonPhase,
          search: debouncedSearch,
        });

        setLeagues(data?.leagues || []);
        setSummary(data?.summary || null);
        setPagination((prev) => ({
          ...prev,
          ...(data?.pagination || {}),
          limit: data?.pagination?.limit || PAGE_SIZE,
        }));
      } catch (err) {
        setLeagues([]);
        setError(err.response?.data?.error || err.message || "Failed to load leagues");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [status, seasonPhase, debouncedSearch]
  );

  const fetchPage = useCallback(
    (pageNum, refresh = false) => {
      setPage(pageNum);
      load({ pageNum, refresh });
    },
    [load]
  );

  useEffect(() => {
    if (deskMode !== "managed") return;
    fetchPage(1);
  }, [status, seasonPhase, debouncedSearch, deskMode, fetchPage]);

  const loadCandidates = useCallback(
    async ({ pageNum = 1, refresh = false } = {}) => {
      setCandidateLoading(true);
      if (refresh) setRefreshing(true);
      setError(null);

      try {
        const data = await fetchAdminLeagueCandidates({
          page: pageNum,
          limit: PAGE_SIZE,
          seasonPhase,
          search: debouncedSearch,
        });

        setCandidates(data?.candidates || []);
        setCandidateSummary(data?.summary || null);
        setCandidatePagination((prev) => ({
          ...prev,
          ...(data?.pagination || {}),
          limit: data?.pagination?.limit || PAGE_SIZE,
        }));
      } catch (err) {
        setCandidates([]);
        setError(err.response?.data?.error || err.message || "Failed to load candidates");
      } finally {
        setCandidateLoading(false);
        setRefreshing(false);
      }
    },
    [seasonPhase, debouncedSearch]
  );

  const fetchCandidatePage = useCallback(
    (pageNum, refresh = false) => {
      setCandidatePage(pageNum);
      loadCandidates({ pageNum, refresh });
    },
    [loadCandidates]
  );

  useEffect(() => {
    if (deskMode !== "candidates") return;
    fetchCandidatePage(1);
  }, [deskMode, seasonPhase, debouncedSearch, fetchCandidatePage]);

  const handleRefresh = () => {
    if (deskMode === "candidates") {
      loadCandidates({ pageNum: candidatePage, refresh: true });
      return;
    }
    load({ pageNum: page, refresh: true });
  };

  const handleDiscoverCandidates = async () => {
    setDiscovering(true);
    try {
      const data = await discoverAdminLeagueCandidates(20);
      if (data.added > 0) {
        toast.success(`Discovered ${data.added} new candidate league${data.added === 1 ? "" : "s"}`);
      } else {
        toast.info("No new candidate leagues found on this pass");
      }
      fetchCandidatePage(1, true);
    } catch (err) {
      toast.error(err.response?.data?.error || "Discover failed");
    } finally {
      setDiscovering(false);
    }
  };

  const displayLeagues = useMemo(() => leagues, [leagues]);

  const selectedLeague = useMemo(() => {
    if (!selectedId) return null;
    return displayLeagues.find((l) => l._id === selectedId) || null;
  }, [displayLeagues, selectedId]);

  useEffect(() => {
    if (!displayLeagues.length) {
      setSelectedId(null);
      return;
    }
    const stillVisible = selectedId && displayLeagues.some((l) => l._id === selectedId);
    if (!stillVisible) {
      setSelectedId(displayLeagues[0]._id);
    }
  }, [displayLeagues, selectedId]);

  const handleLeagueUpdated = (league) => {
    setLeagues((rows) => rows.map((row) => (row._id === league._id ? league : row)));
  };

  const handleToggleEnabled = async (nextEnabled, linkedActivity = null, { force = false } = {}) => {
    if (!selectedLeague) return;

    setBusyId(selectedLeague._id);
    try {
      const body = { isEnabled: nextEnabled };
      if (!nextEnabled && force) body.forceDisable = true;

      const data = await updateAdminLeague(selectedLeague._id, body);
      toast.success(
        nextEnabled
          ? `${selectedLeague.name} enabled`
          : force
            ? `${selectedLeague.name} force-disabled`
            : `${selectedLeague.name} disabled`
      );
      handleLeagueUpdated(data.league);
      setSummary((prev) =>
        prev
          ? {
              ...prev,
              enabled: prev.enabled + (nextEnabled ? 1 : -1),
              disabled: prev.disabled + (nextEnabled ? -1 : 1),
            }
          : prev
      );
    } catch (err) {
      if (err.response?.status === 409 && err.response?.data?.linkedActivity) {
        toast.error(err.response?.data?.error || "Cannot disable league with linked activity");
      } else {
        toast.error(err.response?.data?.error || "Failed to update league");
      }
    } finally {
      setBusyId(null);
    }
  };

  const selectedCandidate = useMemo(() => {
    if (!candidateSelectedId) return null;
    return candidates.find((row) => row._id === candidateSelectedId) || null;
  }, [candidates, candidateSelectedId]);

  useEffect(() => {
    if (deskMode !== "candidates" || !candidates.length) {
      if (deskMode !== "candidates") return;
      setCandidateSelectedId(null);
      return;
    }
    const stillVisible =
      candidateSelectedId && candidates.some((row) => row._id === candidateSelectedId);
    if (!stillVisible) {
      setCandidateSelectedId(candidates[0]._id);
    }
  }, [candidates, candidateSelectedId, deskMode]);

  const handleCandidatePromoted = (league, candidateId) => {
    setCandidates((rows) => rows.filter((row) => row._id !== candidateId));
    setCandidateSummary((prev) =>
      prev ? { ...prev, total: Math.max((prev.total || 1) - 1, 0) } : prev
    );
    setCandidateSelectedId(null);
    if (league?._id) {
      setDeskMode("managed");
      fetchPage(1, true);
      setSelectedId(league._id);
    }
  };

  const handleAdded = (league) => {
    fetchPage(1);
    if (league?._id) setSelectedId(league._id);
  };

  const handleDeskModeChange = (nextMode) => {
    setDeskMode(nextMode);
    setError(null);
  };

  const activeFilterCount =
    deskMode === "candidates"
      ? [seasonPhase !== "all"].filter(Boolean).length
      : [status !== "all", seasonPhase !== "all"].filter(Boolean).length;

  const handleClearFilters = () => {
    setStatus("all");
    setSeasonPhase("all");
    setSearch("");
  };

  const railProps = {
    deskMode,
    onDeskModeChange: handleDeskModeChange,
    status,
    seasonPhase,
    summary,
    candidateSummary,
    resultCount: deskMode === "candidates" ? candidates.length : displayLeagues.length,
    onStatusChange: setStatus,
    onSeasonPhaseChange: setSeasonPhase,
    onClear: handleClearFilters,
    activeCount: activeFilterCount,
  };

  const isLoading =
    deskMode === "candidates"
      ? candidateLoading && !refreshing && candidates.length === 0
      : loading && !refreshing && leagues.length === 0;

  return (
    <div className="am-page alg-page">
      <div className="am-aurora" aria-hidden>
        <span className="am-aurora-a alg-aurora" />
      </div>

      <AdminLeaguesChrome
        deskMode={deskMode}
        onDeskModeChange={handleDeskModeChange}
        search={search}
        onSearchChange={setSearch}
        loading={deskMode === "candidates" ? candidateLoading || refreshing : loading || refreshing}
        onRefresh={handleRefresh}
        onAdd={() => setAddOpen(true)}
        onDiscover={handleDiscoverCandidates}
        discovering={discovering}
        filtersOpen={filtersOpen}
        onFiltersToggle={() => setFiltersOpen((v) => !v)}
        activeFilterCount={activeFilterCount}
        summary={summary}
        candidateSummary={candidateSummary}
        totalLoaded={
          deskMode === "candidates"
            ? candidatePagination.total || candidates.length
            : pagination.total || leagues.length
        }
      />

      <AdminDesk
        sectionKey="leagues"
        sidebarLabel="League filters"
        sidebar={<AdminLeaguesFiltersRail {...railProps} />}
      >
            {error ? (
              <div className="alg-error-banner" role="alert">
                <span>{error}</span>
                <button type="button" onClick={() => setError(null)}>
                  Dismiss
                </button>
              </div>
            ) : null}

            <div className="am-board am-board-floor">
              {isLoading ? (
                <div className="am-state">
                  <Loader2 size={28} className="am-spin" />
                  <p>{deskMode === "candidates" ? "Loading candidates…" : "Loading leagues…"}</p>
                </div>
              ) : deskMode === "candidates" ? (
                error && !candidates.length ? (
                  <div className="am-state">
                    <CloudOff size={28} />
                    <p>{error}</p>
                    <button type="button" className="am-chrome-btn am-chrome-btn-accent" onClick={handleRefresh}>
                      Try again
                    </button>
                  </div>
                ) : candidates.length === 0 && !candidateLoading ? (
                  <div className="am-state">
                    <Radar size={28} />
                    <h2>No candidates yet</h2>
                    <p>Discover up to 20 active API-Sports leagues with a current season.</p>
                    <button
                      type="button"
                      className="am-chrome-btn am-chrome-btn-accent"
                      onClick={handleDiscoverCandidates}
                      disabled={discovering}
                    >
                      {discovering ? "Discovering…" : "Discover 20"}
                    </button>
                  </div>
                ) : (
                  <AdminFloorGrid
                    sectionKey="leagues-candidates"
                    index={
                      <LeagueCandidatesIndex
                        candidates={candidates}
                        selectedId={candidateSelectedId}
                        onSelect={setCandidateSelectedId}
                        pagination={candidatePagination}
                        loading={candidateLoading}
                        onPageChange={(p) => fetchCandidatePage(p)}
                      />
                    }
                    detail={
                      <LeagueCandidateDetail
                        candidate={selectedCandidate}
                        onPromoted={handleCandidatePromoted}
                        onUpdated={(updated) =>
                          setCandidates((rows) =>
                            rows.map((row) => (row._id === updated._id ? updated : row))
                          )
                        }
                      />
                    }
                  />
                )
              ) : error && !leagues.length ? (
                <div className="am-state">
                  <CloudOff size={28} />
                  <p>{error}</p>
                  <button type="button" className="am-chrome-btn am-chrome-btn-accent" onClick={handleRefresh}>
                    Try again
                  </button>
                </div>
              ) : displayLeagues.length === 0 && !loading ? (
                <div className="am-state">
                  <Trophy size={28} />
                  <h2>No leagues match</h2>
                  <p>Add a league from API-Sports or clear your filters.</p>
                  <button type="button" className="am-chrome-btn am-chrome-btn-accent" onClick={() => setAddOpen(true)}>
                    Add league
                  </button>
                </div>
              ) : (
                <AdminFloorGrid
                  sectionKey="leagues"
                  index={
                    <LeaguesFloorIndex
                      leagues={displayLeagues}
                      selectedId={selectedId}
                      onSelect={setSelectedId}
                      pagination={pagination}
                      loading={loading}
                      onPageChange={(p) => fetchPage(p)}
                      groupBySeason={seasonPhase === "all"}
                    />
                  }
                  detail={
                    <LeaguesFloorDetail
                      league={selectedLeague}
                      busy={busyId === selectedLeague?._id}
                      onUpdated={handleLeagueUpdated}
                      onToggleEnabled={handleToggleEnabled}
                    />
                  }
                />
              )}
            </div>
      </AdminDesk>

      {filtersOpen ? (
        <div className="am-filters-backdrop" onClick={() => setFiltersOpen(false)} role="presentation">
          <div className="am-filters-drawer" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="League filters">
            <AdminLeaguesFiltersRail {...railProps} compact onClose={() => setFiltersOpen(false)} />
          </div>
        </div>
      ) : null}

      <AddLeagueModal open={addOpen} onClose={() => setAddOpen(false)} onAdded={handleAdded} />
    </div>
  );
}
